<template>
  <Transition name="header-top-slide">
    <div 
      v-if="isHeaderTopVisible" 
      class="px-6 py-2 bg-blue-50 flex justify-between items-center flex-wrap relative header-top-container"
    >
      <div class="flex space-x-2">
        <DjCurrencySwitcher />
        <DjLangSwitcher />
      </div>
      <div class="flex space-x-2 items-center">
        <USelectMenu
          v-if="hasMultipleAccounts"
          v-model="currentAccount"
          :items="userAccountsList"
          size="sm"
          label-key="name"
          :search-input="{
            placeholder: t('DjHeader.searchAccount'),
            required: true,
          }"
        >
          <UButton variant="ghost" class="flex items-center shadow-none text-gray-900">
            <UIcon name="heroicons:building-storefront" class="w-5 h-5" />
            <span class="max-w-[120px] truncate">
              {{ userAccountSelected ? getCapitalize(userAccountSelected.name) : '' }}
            </span>
          </UButton>
        </USelectMenu>
        <USelectMenu
          v-if="isMultiStores && storesList?.length > 1"
          v-model="currentStore"
          :items="storesList"
          size="xs"
          label-key="STORE_ID"
        >
          <UButton variant="ghost" class="flex items-center shadow-none text-gray-900">
            <UIcon name="flowbite:landmark-outline" class="w-5 h-5" />
            <span>
              {{ userStoreSelected ? getCapitalize(userStoreSelected?.storeId) : '' }}
            </span>
          </UButton>
        </USelectMenu>
        <!-- Bouton de fermeture du header-top -->
        <UButton 
          variant="ghost" 
          size="xs" 
          class="text-gray-600 hover:text-gray-900 p-1 ml-2 transition-colors duration-200"
          @click="hideHeaderTop"
        >
          <UIcon name="heroicons:x-mark" class="w-4 h-4" />
        </UButton>
      </div>
    </div>
  </Transition>


  <header class="sticky top-0 z-10 bg-white">
    <DjHeaderMain />
  </header>
</template>

<script lang="ts" setup>
  // Define Options
  defineOptions({
    name: 'DjHeader',
  });
  const config = useRuntimeConfig();
  const useStore = useUserStore();
  const storyblokStore = useStoryblokStore();
  const { switchCustomerAccount } = useAccount();
  const { getStores } = useDjustAuth();
  const { getCapitalize } = useUtils();
  const { t } = useI18n();
  const storesList = await getStores();
  const userAccountsList = computed(() => useStore.accounts);
  const userStoreSelected = computed(() => useStore.store);
  const userAccountSelected = computed(() => useStore.account);
  const isMultiStores = config?.public.multiStores;
  const currentStore = ref(userStoreSelected.value);
  const currentAccount = ref(userAccountSelected.value);
  
  // Header-top state management
  const isHeaderTopVisible = ref(false);
  
  const showHeaderTop = () => {
    isHeaderTopVisible.value = true;
  };
  
  const hideHeaderTop = () => {
    isHeaderTopVisible.value = false;
  };
  
  const toggleHeaderTop = () => {
    isHeaderTopVisible.value = !isHeaderTopVisible.value;
  };

  // Exposer les fonctions pour que DjHeaderMain puisse les utiliser
  provide('headerTopControls', {
    showHeaderTop,
    hideHeaderTop,
    toggleHeaderTop
  });
  
  // Debug pour voir les comptes
  watch(userAccountsList, (newAccounts) => {
    console.log('👤 userAccountsList changed:', newAccounts);
    console.log('👤 Has multiple accounts:', Array.isArray(newAccounts) && newAccounts.length > 1);
  }, { immediate: true });

  // Computed pour vérifier s'il y a plusieurs comptes
  const hasMultipleAccounts = computed(() => {
    const accounts = userAccountsList.value;
    const result = Array.isArray(accounts) && accounts.length > 1;
    console.log('👤 hasMultipleAccounts computed:', { accounts, result });
    return result;
  });
  
  // Fetch Header Data from Storyblok (SSR)
  const { data: header } = await useAsyncData(() =>
    storyblokStore.fetchStory('header').then(() => storyblokStore.getStoryByName('header'))
  );
  const isAuthenticated = computed(() => useStore.isAuthenticated);
  // Conditional Values for Header Bottom
  const email = computed(() => header.value?.email);
  const phone = computed(() => header.value?.phone);
  const nameAdv = computed(() => header.value?.name);

  async function changeStore(value: any) {
    switchCustomerAccount({ storeId: value?.STORE_ID, storeViewId: value?.STORE_VIEW_ID });
    if (window.location.pathname === '/') {
      location.reload();
    } else {
      window.location.href = '/';
    }
  }
  async function changeAccount(value: any) {
    switchCustomerAccount({ customerAccountId: value.id });
    if (window.location.pathname === '/') {
      location.reload();
    } else {
      window.location.href = '/';
    }
  }
  watch([currentStore], () => {
    changeStore(currentStore.value);
  });
</script>

<style scoped lang="scss">
  .dj-block--user-account {
    position: relative;
    i {
      font-size: 30px;
      line-height: 1em;
    }
    .dj-list--arrow {
      li {
        cursor: pointer;
        padding: 10px;
        a {
          padding-left: 0;
          &:before {
            display: none;
          }
        }
      }
    }

    .dj-block__content {
      background-color: #fff;
      position: absolute;
      min-width: 240px;
      right: 0;
      z-index: 30;
      padding-top: 10px;
      transform: translate(0 0);
      transition: all 0.4s ease;
      visibility: hidden;
      &.order-types {
        min-width: 140px;
      }
    }
    &:hover {
      i {
        cursor: pointer;
      }
      .dj-block__content {
        visibility: visible;
        transform: translate(0 20px) scale3d(1, 1, 1);
        padding: 10px 15px;
      }
    }
  }
   // Animations pour le header-top
   .header-top-slide-enter-active {
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  
  .header-top-slide-leave-active {
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  
  .header-top-slide-enter-from {
    transform: translateY(-100%);
    opacity: 0;
  }
  
  .header-top-slide-leave-to {
    transform: translateY(-100%);
    opacity: 0;
  }
  
  .header-top-slide-enter-to,
  .header-top-slide-leave-from {
    transform: translateY(0);
    opacity: 1;
  }

  // Style pour le conteneur du header-top
  .header-top-container {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(59, 130, 246, 0.2);
  }
</style>
